import React, { useRef, useLayoutEffect } from 'react';
import { MdClose } from 'react-icons/md';
import useDraggable from '@modalSrc/useDraggable'; // Adjust path
import useEscapeKey from '@modalSrc/useEscapeKey'; // Adjust path
import useResizable from '@modalSrc/useResizable'; // New import

/**
 * Modal - a reusable modal dialog component
 *
 * Props:
 * - isOpen: boolean
 * - title: string | ReactNode
 * - onClose: function
 * - children: ReactNode (modal body)
 * - footer: ReactNode (optional modal footer, e.g. buttons)
 * - className: string (optional, for modal box)
 * - overlayClassName: string (optional, for overlay)
 * - zIndex: number | string (optional, applied to .modalOverlay)
 * - size: string (optional, modal size: 'sm', 'md', 'lg', 'xl', 'full')
 */
export default function Modal({
    isOpen,
    title,
    onClose,
    children,
    footer = null,
    className = '',
    overlayClassName = '',
    zIndex = undefined,
    size = '', // New size prop
    bodyClassName = '',
    updateSpinner = null, // Optional spinner update function
    headerActions = [], // Optional titlebar actions rendered before close button
    headerContent = null, // Optional custom titlebar content rendered before header actions
    captionLeft = null,
    captionCenter = null,
    captionRight = null,
}) {
    // All hooks must be called unconditionally, before any return
    const modalRef = useRef(null);
    const { position, isDragged, onMouseDown: onDragMouseDown, reset } = useDraggable(modalRef);
    const { onMouseDown: onResizeMouseDown } = useResizable(modalRef);
    useEscapeKey(onClose, isOpen);
    useLayoutEffect(() => {
        if (isOpen) {
            reset(); // Only call when isOpen transitions to true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    let titleCount = 0;
    let displayTitle = title;

    function handleHeaderMouseDown(e) {
        const target = e.target;
        if (target?.closest?.('.titleActions, .closeButton, .headerActionButton, button, a, input, select, textarea, [data-no-drag="true"]')) {
            return;
        }
        onDragMouseDown(e);
    }

    if (typeof title === 'string') {
        const match = title.match(/^(.*)\s*\((\d+)\)$/);
        if (match) {
            displayTitle = match[1].trim();
            titleCount = parseInt(match[2], 10);
        }
    }

    const resolvedCaptionLeft = captionLeft ?? (
        <h3 className="heading">
            {displayTitle}
            {titleCount > 0 && (<span className="count">({titleCount})</span>)}
        </h3>
    );

    if (!isOpen) return null;

    return (
        <div
            className={`modalOverlay ${overlayClassName}`}
            style={zIndex !== undefined ? { zIndex } : undefined}
        >
            <div
                className={`modal ${size ? `modal-${size}` : ''} ${className}`}
                ref={modalRef}
                role="dialog"
                aria-modal="true"
            >
                <div className="modalHeader" onMouseDown={handleHeaderMouseDown}>
                    <div className="modalCaption modalCaptionLeft draggableHandle">
                        {resolvedCaptionLeft}
                    </div>
                    {captionCenter &&
                    <div className="modalCaption modalCaptionCenter">{captionCenter}</div>}
                    {captionRight && 
                    <div className="modalCaption modalCaptionRight">{captionRight}</div>}
                    <span className="titleActions">
                        {headerContent}
                        {Array.isArray(headerActions) && headerActions.map((action, idx) => {
                            if (!action) return null;
                            
                            // If it's a React element (icon component), render it directly in a span
                            if (typeof action === 'object' && action?.$$typeof) {
                                return (
                                    <span key={action.key || `header-action-${idx}`} className="headerActionIcon">
                                        {action}
                                    </span>
                                );
                            }
                            
                            // Otherwise, treat as action object and wrap in button
                            const {
                                key,
                                icon,
                                onClick,
                                title: actionTitle,
                                ariaLabel,
                                className: actionClassName = '',
                                disabled = false,
                            } = action;

                            return (
                                <button
                                    key={key || `header-action-${idx}`}
                                    type="button"
                                    className={`headerActionButton ${actionClassName}`.trim()}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (typeof onClick === 'function') onClick(e);
                                    }}
                                    title={actionTitle}
                                    aria-label={ariaLabel || actionTitle || 'Header action'}
                                    disabled={disabled}
                                >
                                    {icon}
                                </button>
                            );
                        })}
                        <MdClose
                            className="actionIcon actionButton closeButton"
                            data-testid="close-icon"
                            size={24}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onClose(); }}
                        />
                    </span>
                </div>
                <div className={`modalBody ${bodyClassName}`.trim()}>{children}</div>
                {footer && <div className="modalFooter">{footer}</div>}
                <div
                    className="resizeHandle"
                    onMouseDown={onResizeMouseDown}
                />
            </div>
        </div>
    );
}